import { fetchConfig, parseFixedJSON } from "@theme/utilities";
import { CartErrorEvent, ThemeEvents, CartAddEvent } from "@theme/events";
import { getProgressBarId } from "@theme/progress-bar";

/**
 * Intelligems FULLGEAR test. Injected as `window.igFullGear = true` in the cart drawer.
 * @param {unknown} [value]
 * @returns {boolean}
 */
function isIgFullGearFlagOn(value = window.igFullGear) {
  return value === true || value === "true" || value === 1;
}

/**
 * @returns {boolean}
 */
export function isIgFullGearEnabled() {
  if (document.documentElement.getAttribute("data-ig-full-gear") === "1") {
    return true;
  }
  return isIgFullGearFlagOn();
}

const FULL_GEAR_CONFIRM_COPY = {
  extras: {
    body: "The 3 extra knives and the free whetstone come out. Your original 3 knives and free Zubo stay.",
    action: "Remove extras, keep my 3 knives",
  },
  zuboChoice: {
    body: "This knife unlocked your free Zubo, so the free Zubo, the 3 extra knives, and the free whetstone go with it. Your first 2 knives stay.",
    action: "Remove this knife and free Zubo",
  },
  base: {
    body: "This is one of your original knives, so the extra knives, free Zubo, and free whetstone go with it. Your other original knife stays.",
    action: "Remove this and the extra knives",
  },
};

/**
 * Added-for-you knives use the extras warning.
 * The knife added with free Zubo uses its own warning.
 * The original paid knives use the base warning.
 * @param {Element | null | undefined} row
 * @returns {"extras" | "zuboChoice" | "base"}
 */
export function fullGearConfirmMode(row) {
  const ladder = (row?.getAttribute("data-cart-ladder") || "").trim();
  if (ladder === "rung_2_choice" || row?.getAttribute("data-rung-2-choice") === "1") {
    return "zuboChoice";
  }
  if (row?.getAttribute("data-ladder-base") === "1") return "base";
  return "extras";
}

/**
 * @param {Element} confirm
 * @param {Element | null | undefined} row
 */
export function fillFullGearConfirmCopy(confirm, row) {
  const mode = fullGearConfirmMode(row);
  const copy = FULL_GEAR_CONFIRM_COPY[mode];
  const now = confirm.getAttribute("data-full-gear-now") || "$649";
  const instead = confirm.getAttribute("data-paid-instead") || "";
  const insteadNode = confirm.querySelector("[data-full-gear-confirm-instead]");
  const nowNode = confirm.querySelector("[data-full-gear-confirm-now]");
  const body = confirm.querySelector("[data-full-gear-confirm-copy]");
  const action = confirm.querySelector("[data-full-gear-confirm-action]");
  if (insteadNode && instead) insteadNode.textContent = instead;
  if (nowNode) nowNode.textContent = now;
  if (body) body.textContent = copy.body;
  if (action) action.textContent = copy.action;
}

/**
 * Horizon `on:click` is a document capture listener registered by the first
 * Component on the page, so the ladder click handler often runs too late.
 * Call this from `onLineItemRemove` before qty 0 is sent.
 * @param {Element | null | undefined} row
 * @returns {boolean}
 */
export function interceptFullGearLineRemove(row) {
  if (!isIgFullGearEnabled() || !(row instanceof Element)) return false;
  const line = row.closest("tr[data-key]") || row;
  if (!(line instanceof Element)) return false;
  if (line.getAttribute("data-full-gear-gift") === "1") return false;
  if (line.getAttribute("data-full-gear-paid") !== "1") return false;

  const confirm = document.querySelector("[data-full-gear-confirm]");
  if (!confirm) return false;

  fillFullGearConfirmCopy(confirm, line);
  document.documentElement.setAttribute("data-ig-full-gear", "1");
  confirm.hidden = false;
  const scroller =
    confirm.closest(".cart-drawer__content") ||
    document.querySelector(".cart-drawer__content");
  if (scroller) scroller.scrollTo({ top: 0, behavior: "smooth" });

  document.dispatchEvent(
    new CustomEvent("cart-ladder:confirm-remove", { detail: { row: line } }),
  );
  return true;
}

/**
 * The knife added with free Zubo and the Zubo line leave together.
 * Full Gear confirm handles its own path; this covers a normal trash click.
 * @param {Element | null | undefined} row
 * @returns {boolean}
 */
export function removeRungTwoChoiceAndZubo(row) {
  const line = row instanceof Element ? row.closest("tr[data-key]") || row : null;
  if (!(line instanceof Element)) return false;
  if (line.getAttribute("data-rung-2-choice") !== "1") return false;

  const choiceKey = line.getAttribute("data-key") || "";
  if (!choiceKey) return false;

  /** @type {Record<string, number>} */
  const updates = { [choiceKey]: 0 };
  document.querySelectorAll("[data-rung-2-zubo='1']").forEach((zuboRow) => {
    const key = zuboRow.getAttribute("data-key");
    if (key) updates[key] = 0;
  });

  new CartActions().updateCartState({ updates });
  return true;
}

const igFullGearListeners = [];
let igFullGearWatchStarted = false;

function flushIgFullGearListeners() {
  if (!isIgFullGearFlagOn()) return;
  document.documentElement.setAttribute("data-ig-full-gear", "1");
  const queued = igFullGearListeners.splice(0);
  queued.forEach((fn) => fn());
}

/**
 * Reveal ladder UI and run once the Intelligems flag is on (it may inject late).
 * @param {() => void} [callback]
 */
export function whenIgFullGearEnabled(callback) {
  if (typeof callback === "function") igFullGearListeners.push(callback);

  if (isIgFullGearFlagOn()) {
    flushIgFullGearListeners();
    return;
  }

  if (igFullGearWatchStarted) return;
  igFullGearWatchStarted = true;

  let stored = window.igFullGear;
  try {
    Object.defineProperty(window, "igFullGear", {
      configurable: true,
      get() {
        return stored;
      },
      set(value) {
        stored = value;
        if (isIgFullGearFlagOn(value)) flushIgFullGearListeners();
      },
    });
  } catch {
    // Property may already exist; poll instead.
  }

  const pollId = setInterval(() => {
    if (isIgFullGearFlagOn()) {
      clearInterval(pollId);
      flushIgFullGearListeners();
    }
  }, 100);
  setTimeout(() => clearInterval(pollId), 15000);
}

/**
 * Ladder Liquid mutations stay inert for the control group.
 * @param {Element} script
 */
function allowJsonMutationScript(script) {
  if (!script?.hasAttribute?.("data-ig-full-gear-mutation")) return true;
  return isIgFullGearEnabled();
}

/**
 * Sets the loading state on the cart drawer
 * @param {boolean} [isLoading=true] - Whether to set or clear the loading state
 * @returns {void}
 */
export function setCartDrawerLoadingState(isLoading = true) {
  const cartDrawer = document.querySelector(".cart-drawer");
  if (!cartDrawer) return;
  if (isLoading) {
    cartDrawer.classList.add("loading");
  } else {
    cartDrawer.classList.remove("loading");
  }
}

/**
 * Reads the authoritative cart item count out of a rendered cart drawer section.
 *
 * The drawer renders the total into a hidden `[ref="cartItemCount"]` element, but
 * only on the non-empty branch of `snippets/cart-products.liquid` - an empty cart
 * omits it entirely, which is why a missing element means 0 rather than unknown.
 *
 * @param {Record<string, string> | undefined | null} sections - Section HTML keyed by section id, as returned by the cart AJAX API
 * @returns {number | null} The cart item count, or null if the drawer section is not in the response
 */
export function getCartItemCountFromSections(sections) {
  const sectionId = getCartDrawerSectionId();
  const html = sectionId && sections ? sections[sectionId] : null;
  if (typeof html !== "string") return null;

  const sectionHTML = new DOMParser().parseFromString(html, "text/html");
  const hiddenItemCount = sectionHTML.querySelector(
    '[ref="cartItemCount"]',
  )?.textContent;

  // Absent element = empty cart (see above), so fall back to 0, not to null.
  if (!hiddenItemCount) return 0;

  const itemCount = parseInt(hiddenItemCount, 10);
  return Number.isFinite(itemCount) ? itemCount : null;
}

/**
 * Syncs every cart icon bubble in the page to a known cart item count.
 *
 * `<cart-icon>` normally updates itself on `ThemeEvents.cartUpdate`, but the
 * actions in this file re-render the drawer directly instead of dispatching that
 * event, and the icon sits outside `.cart-drawer__dialog` — so the innerHTML swap
 * in `#renderCartSection` never reaches it and it has to be told explicitly.
 * Delegating to the element's own `renderCartBubble` keeps the count text, the
 * empty-cart `hidden`/`visually-hidden` toggles, the `--has-cart` class and the
 * `cart-count` sessionStorage mirror owned by one place (`cart-icon.js`).
 *
 * @param {number} itemCount - The number of items in the cart
 * @returns {void}
 */
export function syncCartIconBubbles(itemCount) {
  if (!Number.isFinite(itemCount)) return;

  document.querySelectorAll("cart-icon").forEach((cartIcon) => {
    const icon = /** @type {any} */ (cartIcon);
    // Not upgraded yet (cart-icon.js is a low-priority module) - nothing to call.
    if (typeof icon.renderCartBubble !== "function") return;
    // `false` = set the count, do not add to it (that is product-form behaviour).
    icon.renderCartBubble(itemCount, false);
  });
}

/**
 * Gets the cart drawer section ID
 * @returns {any} The cart drawer section ID
 */
export function getCartDrawerSectionId() {
  const headerSectionId = document.querySelector("#header-group > header")?.id;
  if (!headerSectionId) return null;
  return headerSectionId.replace("shopify-section-", "");
}

/**
 * Gets the mutations related to adding line items to the cart
 * @returns {any[]} The array of mutations
 */
export function getAddCartLineItemMutations() {
  const progressBarId = getProgressBarId();

  const items = Array.from(
    document.querySelectorAll('script[data-json="addLineItem"]'),
  ).reduce((newlineItems, item) => {
    // @ts-ignore
    const itemProgressBarId = item.dataset?.progressBarId || null;
    if (itemProgressBarId && itemProgressBarId !== progressBarId) {
      return newlineItems;
    }
    if (!allowJsonMutationScript(item)) {
      return newlineItems;
    }

    try {
      const dataJson = parseFixedJSON(item.textContent);
      newlineItems.push(dataJson);
    } catch {
      console.warn("AddLineItem data parsing is failed");
    }

    return newlineItems;
  }, /** @type {any[]} */ ([]));

  const uniqueItems = [];
  const seenIds = new Set();

  for (const item of items) {
    const id = Number(item?.id);
    if (!Number.isFinite(id) || seenIds.has(id)) continue;
    seenIds.add(id);
    uniqueItems.push({ ...item, id, quantity: 1 });
  }

  return uniqueItems;
}

/**
 * Gets the mutations related to changing line items in the cart
 * @returns {any[]} The array of mutations
 */
export function getChangeLineItemMutations() {
  const items = Array.from(
    document.querySelectorAll('script[data-json="changeLineItem"]'),
  ).reduce((changeLineItems, item) => {
    if (!allowJsonMutationScript(item)) return changeLineItems;
    try {
      const dataJson = /** @type {any} */ (JSON.parse(item.textContent));
      changeLineItems.push(dataJson);
    } catch {
      console.warn("ChangeLineItem data parsing is failed");
    }
    return changeLineItems;
  }, /** @type {any[]} */ ([]));

  const uniqueItems = [];
  const seenIds = new Set();

  for (const item of items) {
    const id = item?.id;
    if (id == null || seenIds.has(id)) continue;
    seenIds.add(id);
    uniqueItems.push(item);
  }

  return uniqueItems;
}

/**
 * Gets cart-attribute mutations emitted by Liquid.
 * @returns {Record<string, string> | null}
 */
export function getUpdateCartAttributeMutations() {
  const scripts = Array.from(
    document.querySelectorAll('script[data-json="updateCartAttributes"]'),
  );
  if (scripts.length === 0) return null;

  const attributes = scripts.reduce((nextAttributes, item) => {
    if (!allowJsonMutationScript(item)) return nextAttributes;
    try {
      Object.assign(nextAttributes, JSON.parse(item.textContent || "{}"));
    } catch {
      console.warn("UpdateCartAttributes data parsing is failed");
    }
    return nextAttributes;
  }, /** @type {Record<string, string>} */ ({}));

  if (Object.keys(attributes).length === 0) return null;
  return attributes;
}

/**
 * Discount code emitted by Liquid (e.g. restore 2NEW after Full Gear strip).
 * @returns {string | null}
 */
export function getApplyDiscountMutation() {
  const scripts = Array.from(
    document.querySelectorAll('script[data-json="applyDiscount"]'),
  );
  for (const script of scripts) {
    if (!allowJsonMutationScript(script)) continue;
    try {
      const data = JSON.parse(script.textContent || "{}");
      if (typeof data?.discount === "string" && data.discount) return data.discount;
      if (typeof data?.code === "string" && data.code) return data.code;
    } catch {
      console.warn("ApplyDiscount data parsing is failed");
    }
  }
  return null;
}

/**
 * Removes a specified query argument from the current URL and updates the browser's history.
 *
 * @param {string} paramName - The name of the query parameter to remove.
 * @param {boolean} replace - Use true to use replaceState (no new history entry), false for pushState (new history entry).
 */
function removeQueryArgFromUrl(paramName, replace = true) {
  // 1. Create a URL object based on the current window location
  const url = new URL(window.location.href);

  // 2. Use URLSearchParams.delete() to remove the specific parameter
  url.searchParams.delete(paramName);

  // 3. Update the browser's URL using the History API
  if (replace) {
    // Modify the current history entry
    history.replaceState(null, "", url.href);
  } else {
    // Add a new entry to the history stack
    history.pushState(null, "", url.href);
  }
}

/**
 * @param {any} item
 */
function isFullGearGiftItem(item) {
  const props = item?.properties || {};
  if (props._gift_purchase === "true" || props._gift_purchase === true) return true;
  if (props._discount_gift) return true;
  if (props._cart_ladder === "full_gear_gift" || props._cart_ladder === "rung_2_zubo") return true;
  return false;
}

/**
 * @param {any} item
 */
function isKitchenKnifeItem(item) {
  return /kitchen\s*knives/i.test(item?.product_type || "");
}

/**
 * Cart actions class
 * */
export class CartActions {
  constructor() {}

  /**
   * Adds items, then sets Full Gear and drops 2NEW in one follow-up update.
   * Existing paid knives are stamped with `_full_gear_lock` first, so a knife
   * added afterwards cannot join that set.
   * The drawer renders only after that second request.
   * @param {any[]} items
   * @param {Function} [callback]
   */
  upgradeFullGear(items, callback) {
    if (!Array.isArray(items) || items.length === 0) return;

    const fetchCfg = fetchConfig("json");
    setCartDrawerLoadingState(true);

    fetch("/cart.js")
      .then((response) => response.json())
      .then((cart) => {
        const addedKnives = items.filter(
          (item) => item?.properties?._cart_ladder === "full_gear_added",
        );
        return this.#lockFullGearLines(cart, 6 - addedKnives.length);
      })
      .then((locked) => {
        if (!locked) return null;

        return fetch("/cart/add.js", {
          ...fetchCfg,
          body: JSON.stringify({ items }),
          headers: {
            ...fetchCfg.headers,
          },
        }).then((response) => response.json());
      })
      .then((addResponse) => {
        if (!addResponse) return null;
        if (addResponse?.status) {
          document.dispatchEvent(
            new CartErrorEvent(
              "cart_actions_add_items",
              addResponse.message,
              addResponse.description,
              addResponse.errors,
            ),
          );
          return null;
        }

        return fetch("/cart.js").then((response) => response.json());
      })
      .then((cart) => {
        if (!cart) return null;

        const remaining = (cart?.discount_codes || [])
          .map((/** @type {{ code?: string }} */ entry) => entry.code)
          .filter(
            (/** @type {string | undefined} */ code) =>
              typeof code === "string" &&
              code.trim().toUpperCase() !== "2NEW",
          );

        return fetch("/cart/update.js", {
          ...fetchCfg,
          body: JSON.stringify({
            attributes: { _full_gear: "1" },
            discount: remaining.join(","),
            sections: getCartDrawerSectionId(),
          }),
          headers: {
            ...fetchCfg.headers,
          },
        });
      })
      .then((response) => {
        if (!response) return null;
        return response.json();
      })
      .then((response) => {
        if (!response) return;
        if (response.status) {
          document.dispatchEvent(
            new CartErrorEvent(
              "cart_actions_update_attributes",
              response.message,
              response.description,
              response.errors,
            ),
          );
          return;
        }
        this.#renderCartSection(response);
        if (callback && typeof callback === "function") {
          callback();
        }
      })
      .catch(() => {
        document.dispatchEvent(
          new CartErrorEvent(
            "cart_actions_add_items",
            "Error",
            "We couldn't add Full Gear. Please try again.",
            {},
          ),
        );
      })
      .finally(() => {
        setCartDrawerLoadingState(false);
      });
  }

  /**
   * Stamp the knives already in the cart. `slots` is how many of them belong
   * in the set of 6, after the offer's own knives are counted.
   * @param {any} cart
   * @param {number} slots
   * @returns {Promise<boolean>}
   */
  async #lockFullGearLines(cart, slots) {
    let remaining = Math.max(0, slots);
    const fetchCfg = fetchConfig("json");

    for (const item of cart?.items || []) {
      if (remaining <= 0) break;
      if (isFullGearGiftItem(item) || !isKitchenKnifeItem(item)) continue;
      if (item?.properties?._cart_ladder === "full_gear_added") continue;

      const qty = Number(item.quantity) || 0;
      if (qty < 1 || !item.key) continue;

      const take = Math.min(qty, remaining);
      const properties = {
        ...(item.properties || {}),
        _full_gear_lock: String(take),
      };

      const response = await fetch("/cart/change.js", {
        ...fetchCfg,
        body: JSON.stringify({
          id: item.key,
          quantity: qty,
          properties,
        }),
        headers: {
          ...fetchCfg.headers,
        },
      }).then((result) => result.json());

      if (response?.status) {
        document.dispatchEvent(
          new CartErrorEvent(
            "cart_actions_update_items",
            response.message,
            response.description,
            response.errors,
          ),
        );
        return false;
      }

      remaining -= take;
    }

    return true;
  }

  /**
   * @param {any[]} items
   * @param {Function} [callback]
   */
  addItems(items, callback) {
    if (!Array.isArray(items) || items.length === 0) return;

    const fetchCfg = fetchConfig("json");

    setCartDrawerLoadingState(true);

    fetch("/cart/add.js", {
      ...fetchCfg,
      body: JSON.stringify({
        items,
        sections: getCartDrawerSectionId(),
      }),
      headers: {
        ...fetchCfg.headers,
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          document.dispatchEvent(
            new CartErrorEvent(
              "cart_actions_add_items",
              response.message,
              response.description,
              response.errors,
            ),
          );
        } else {
          this.#renderCartSection(response);
          if (callback && typeof callback === "function") {
            callback();
          }
        }
      })
      .finally(() => {
        setCartDrawerLoadingState(false);
      });
  }

  /**
   * Remove one line item, then add a replacement variant (update.js + add.js).
   * @param {string} lineKey
   * @param {number} variantId
   * @param {Record<string, string>} properties
   * @param {Function} [callback]
   */
  swapGiftLineItemInCart(lineKey, variantId, properties, callback) {
    const removeFormData = new FormData();
    removeFormData.append(`updates[${lineKey}]`, "0");

    setCartDrawerLoadingState(true);

    fetch("/cart/update.js", {
      method: "POST",
      body: removeFormData,
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          document.dispatchEvent(
            new CartErrorEvent(
              "cart_actions_swap_gift",
              response.message,
              response.description,
              response.errors,
            ),
          );
          return null;
        }

        const fetchCfg = fetchConfig("json");
        return fetch("/cart/add.js", {
          ...fetchCfg,
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1, properties }],
            sections: getCartDrawerSectionId(),
          }),
          headers: {
            ...fetchCfg.headers,
          },
        });
      })
      .then((response) => {
        if (!response) return null;
        return response.json();
      })
      .then((response) => {
        if (!response) return;

        if (response.status) {
          document.dispatchEvent(
            new CartErrorEvent(
              "cart_actions_swap_gift",
              response.message,
              response.description,
              response.errors,
            ),
          );
        } else {
          this.#renderCartSection(response);
          if (callback && typeof callback === "function") {
            callback();
          }
        }
      })
      .finally(() => {
        setCartDrawerLoadingState(false);
      });
  }

  /**
   * @param {any[]} items
   * @param {Function} [callback]
   */
  updateItems(items, callback) {
    if (!Array.isArray(items) || items.length === 0) return;

    const formData = new FormData();

    items.map((item) => {
      formData.append(`updates[${item.id}]`, item.quantity);
    });

    formData.append("sections", getCartDrawerSectionId());

    setCartDrawerLoadingState(true);

    fetch("/cart/update.js", {
      method: "POST",
      body: formData,
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          document.dispatchEvent(
            new CartErrorEvent(
              "cart_actions_update_items",
              response.message,
              response.description,
              response.errors,
            ),
          );
        } else {
          this.#renderCartSection(response);
          if (callback && typeof callback === "function") {
            callback();
          }
        }
      })
      .finally(() => {
        setCartDrawerLoadingState(false);
      });
  }

  /**
   * Renders the cart section
   * @param {any} response - The response object
   */
  #renderCartSection(response) {
    let content = null;

    if (response?.sections && response?.sections[getCartDrawerSectionId()]) {
      content = response?.sections[getCartDrawerSectionId()];
    } else if (response[getCartDrawerSectionId()]) {
      content = response[getCartDrawerSectionId()];
    }

    if (!content) {
      return;
    }

    const cartDrawerDOMParser = new DOMParser();
    const newSectionHTML = cartDrawerDOMParser.parseFromString(
      content,
      "text/html",
    );

    // Get new cart count
    const newCartHiddenItemCount = newSectionHTML.querySelector(
      '[ref="cartItemCount"]',
    )?.textContent;
    const newCartItemCount = newCartHiddenItemCount
      ? parseInt(newCartHiddenItemCount, 10)
      : 0;
    document.querySelectorAll('[ref="cartItemCount"]').forEach((el) => {
      el.textContent = newCartItemCount.toString();
    });

    // `[ref="cartItemCount"]` only exists inside the drawer (and only when the
    // cart is non-empty), so the header cart icon needs the count handed to it -
    // otherwise the bubble keeps its page-load value and still shows a number
    // after the cart has been emptied.
    syncCartIconBubbles(newCartItemCount);

    // Update cart drawer dialog content
    const newCartDrawerDialog = newSectionHTML.querySelector(
      ".cart-drawer__dialog",
    );
    const cartDrawerDialog = document.querySelector(".cart-drawer__dialog");
    if (cartDrawerDialog && newCartDrawerDialog) {
      cartDrawerDialog.innerHTML = newCartDrawerDialog.innerHTML;
      document.dispatchEvent(
        new CustomEvent("RenderCartSection", { detail: response }),
      );
    }
  }

  /**
   * Updates cart attributes
   * @param {object} attributes
   * @param {Function} [callback]
   * @returns
   */
  updateCartAttributes(attributes, callback) {
    this.updateCartState({ attributes }, callback);
  }

  /**
   * One /cart/update.js call for line qty, attributes, and/or discount code.
   * @param {{ updates?: Record<string, number>, attributes?: object, discount?: string }} payload
   * @param {Function} [callback]
   */
  updateCartState(payload, callback) {
    if (!payload || typeof payload !== "object") return;

    const body = /** @type {Record<string, unknown>} */ ({
      sections: getCartDrawerSectionId(),
    });

    if (payload.updates && typeof payload.updates === "object") {
      body.updates = payload.updates;
    }
    if (payload.attributes && typeof payload.attributes === "object") {
      body.attributes = payload.attributes;
    }
    if (typeof payload.discount === "string") {
      body.discount = payload.discount;
    }

    if (!body.updates && !body.attributes && body.discount == null) return;

    const fetchCfg = fetchConfig("json");

    setCartDrawerLoadingState(true);

    fetch("/cart/update.js", {
      ...fetchCfg,
      body: JSON.stringify(body),
      headers: {
        ...fetchCfg.headers,
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          document.dispatchEvent(
            new CartErrorEvent(
              "cart_actions_update_attributes",
              response.message,
              response.description,
              response.errors,
            ),
          );
        } else {
          this.#renderCartSection(response);
          if (callback && typeof callback === "function") {
            callback();
          }
        }
      })
      .finally(() => {
        setCartDrawerLoadingState(false);
      });
  }

  /**
   * Updates the cart drawer section
   */
  updateCartDrawerSection() {
    setCartDrawerLoadingState(true);

    fetch(`/?sections=${getCartDrawerSectionId()}`)
      .then((response) => response.json())
      .then((response) => {
        this.#renderCartSection(response);
      })
      .finally(() => {
        setCartDrawerLoadingState(false);
      });
  }

  /**
   * Applies a discount code to the cart
   * @param {string} code
   */
  applyDiscount(code) {
    if (typeof code !== "string" || !code || code.trim() === "") {
      return;
    }

    console.log("run_discount_apply", code);

    const fetchCfg = fetchConfig("json");

    setCartDrawerLoadingState(true);

    fetch("/cart/update.js", {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
      },
      body: JSON.stringify({
        discount: code,
        sections: getCartDrawerSectionId(),
      }),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          document.dispatchEvent(
            new CartErrorEvent(
              "cart_actions_apply_discount",
              response.message,
              response.description,
              response.errors,
            ),
          );
        } else {
          this.#renderCartSection(response);
        }
      })
      .finally(() => {
        setCartDrawerLoadingState(false);
      });
  }

  /**
   * Removes a discount code from the cart, keeping any other codes.
   * @param {string} code
   * @param {Function} [callback]
   */
  removeDiscountCode(code, callback) {
    if (typeof code !== "string" || !code) {
      if (callback) callback();
      return;
    }

    const fetchCfg = fetchConfig("json");
    const needle = code.trim().toUpperCase();

    setCartDrawerLoadingState(true);

    fetch("/cart.js")
      .then((response) => response.json())
      .then((cart) => {
        const remaining = (cart.discount_codes || [])
          .map((/** @type {{ code?: string }} */ entry) => entry.code)
          .filter(
            (/** @type {string | undefined} */ existing) =>
              typeof existing === "string" &&
              existing.trim().toUpperCase() !== needle,
          );

        return fetch("/cart/update.js", {
          ...fetchCfg,
          body: JSON.stringify({
            discount: remaining.join(","),
            sections: getCartDrawerSectionId(),
          }),
          headers: {
            ...fetchCfg.headers,
          },
        });
      })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          document.dispatchEvent(
            new CartErrorEvent(
              "cart_actions_remove_discount",
              response.message,
              response.description,
              response.errors,
            ),
          );
        } else {
          this.#renderCartSection(response);
        }
        if (callback) callback();
      })
      .catch(() => {
        if (callback) callback();
      })
      .finally(() => {
        setCartDrawerLoadingState(false);
      });
  }
}

/*
 * Detects mutations related to cart line items
 * @returns {boolean} True if mutations are detected, false otherwise
 */
let giftComboInProgress = false;

function detectCartLineItemsMutations() {
  if (giftComboInProgress) {
    return false;
  }

  const changeLineItems = getChangeLineItemMutations();
  const newlineItems = getAddCartLineItemMutations();
  const attributeUpdates = getUpdateCartAttributeMutations();
  const discountCode = getApplyDiscountMutation();
  const cartActions = new CartActions();

  let hasChanges = false;

  if (
    changeLineItems.length > 0 ||
    newlineItems.length > 0 ||
    attributeUpdates ||
    discountCode
  ) {
    setCartDrawerLoadingState(true);
    hasChanges = true;
  }

  const applyCombinedState = (callback) => {
    /** @type {Record<string, number>} */
    const updates = {};
    for (const item of changeLineItems) {
      const id = String(item?.id || "");
      if (!id) continue;
      updates[id] = Number(item.quantity);
    }

    cartActions.updateCartState(
      {
        ...(Object.keys(updates).length > 0 ? { updates } : {}),
        ...(attributeUpdates ? { attributes: attributeUpdates } : {}),
        ...(discountCode ? { discount: discountCode } : {}),
      },
      callback,
    );
  };

  const applyAttributeUpdates = (callback) => {
    if (!attributeUpdates) {
      if (callback) callback();
      return;
    }
    cartActions.updateCartAttributes(attributeUpdates, callback);
  };

  const applyLineMutations = (callback) => {
    if (changeLineItems.length > 0 && newlineItems.length == 0) {
      cartActions.updateItems(changeLineItems, callback);
    } else if (newlineItems.length > 0 && changeLineItems.length == 0) {
      cartActions.addItems(newlineItems, callback);
    } else if (changeLineItems.length > 0 && newlineItems.length > 0) {
      cartActions.updateItems(changeLineItems, () => {
        setTimeout(() => cartActions.addItems(newlineItems, callback), 500);
      });
    } else if (callback) {
      callback();
    }
  };

  if (hasChanges) {
    setTimeout(() => {
      if (newlineItems.length === 0 && (attributeUpdates || discountCode)) {
        applyCombinedState();
        return;
      }
      applyAttributeUpdates(() => applyLineMutations());
    }, 500);
  } else {
    setCartDrawerLoadingState(false);
  }

  return hasChanges;
}

/**
 * Detects cart rows attributes and updates cart if needed
 * @returns {boolean} True if updates were made, false otherwise
 */
function detectCartRowsAttributes() {
  let hasChanges = false;

  //@ts-ignore
  const items = Array.from(
    document.querySelectorAll('tr[ref^="cartItemRows"][data-progress-bar-id]'),
  ).reduce((items, item) => {
    const progressBarId = item.getAttribute("data-progress-bar-id");
    const currentProgressBarId = getProgressBarId();

    if (progressBarId && progressBarId !== currentProgressBarId) {
      //@ts-ignore
      items.push({
        id: item.getAttribute("data-key"),
        quantity: 0,
      });
    }

    return items;
  }, []);

  if (items.length > 0) {
    const cartActions = new CartActions();
    setCartDrawerLoadingState(true);
    cartActions.updateItems(items);
    hasChanges = true;
  }

  return hasChanges;
}

/**
 * Cart loaded event handler
 */
function handleCartLoaded() {
  const cartActions = new CartActions();
  const cartForm = document.querySelector("#cart-form");
  const progressBarId = getProgressBarId();

  let hasChanges = detectCartLineItemsMutations();

  // if(!hasChanges)
  // {
  //   const cartProgressBarId = cartForm?.getAttribute('data-progress-bar-id') || null;
  //   if(cartForm && progressBarId && cartProgressBarId !== progressBarId) {
  //     cartActions.updateCartAttributes({
  //       '_progress_bar_id': progressBarId
  //     });
  //   }
  // }
}

/**
 * Detects the 'cart' query argument and opens the cart drawer if present
 */
function detectCartQueryArgument() {
  const pageArgs = new URLSearchParams(window.location.search);
  const cartDrawer = document.querySelector(".cart-drawer__dialog");
  // @ts-ignore
  if (window["igCartDrawer"] === true) {
    setTimeout(() => {
      updateGiftComboVisibility();
    }, 2000);
  }
  if (pageArgs.has("cart")) {
    if (cartDrawer) {
      removeQueryArgFromUrl("cart", true);
      new CartActions().updateCartDrawerSection();
      //@ts-ignore
      cartDrawer.showModal();
    }
  }
}

const discount_2FREE_collections = [645422711147];
const discount_2NEW_collections = [
  646660882795, 647181042027, 647511867755, 648066105707, 648150483307,
  648440349035, 648601502059, 650526687595, 653211730283,655028224363
];
const discount_BARK_collections = [648785035627];
const discount_NEWSHP_collections = [648856994155];
const discount_MYSTRY_collections = [648858206571];
const discount_FREEBUNDLE_collections = [649499836779];

function applyDiscountCodeByRequest() {
  const cartActions = new CartActions();

  if (document.querySelector('[data-full-gear-cart="1"]')) {
    return;
  }

  if (discount_2FREE_collections.includes(Theme.collection?.id)) {
    cartActions.applyDiscount("2FREE");
  } else if (discount_2NEW_collections.includes(Theme.collection?.id)) {
    cartActions.applyDiscount("2NEW");
  } else if (discount_BARK_collections.includes(Theme.collection?.id)) {
    cartActions.applyDiscount("BARK");
  } else if (discount_MYSTRY_collections.includes(Theme.collection?.id)) {
    cartActions.applyDiscount("MYSTRY");
  } else if (discount_NEWSHP_collections.includes(Theme.collection?.id)) {
    cartActions.applyDiscount("NEWSHP");
  } else if (discount_FREEBUNDLE_collections.includes(Theme.collection?.id)) {
    cartActions.applyDiscount("FREEBUNDLE");
  } else if (Theme.page?.id == 711216005483) {
    cartActions.applyDiscount("2NEW");
  }
}

function onDocumentReady() {
  handleCartLoaded();
  observeCartLineItemInlineErrors();
  detectCartQueryArgument();
  applyDiscountCodeByRequest();
  updateGiftComboVisibility();
  document.dispatchEvent(new CustomEvent("cartDrawerAddon:render"));
  whenIgFullGearEnabled(() => {
    handleCartLoaded();
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", onDocumentReady);
} else {
  onDocumentReady();
}

window.addEventListener("load", () => {
  setTimeout(() => {
    handleCartLoaded();
    updateGiftComboVisibility();
    document.dispatchEvent(new CustomEvent("cartDrawerAddon:render"));
  }, 300);
});

window.addEventListener("pageshow", () => {
  updateGiftComboVisibility();
});

document.addEventListener("cartItems:mutated", () => {
  updateGiftComboVisibility();
});

document.addEventListener(ThemeEvents.cartUpdate, () => {
  setTimeout(() => {
    handleCartLoaded();
    document.dispatchEvent(new CustomEvent("cartDrawerAddon:render"));
  }, 300);
  setCartDrawerLoadingState(false);
});

document.addEventListener(ThemeEvents.discountUpdate, () => {
  setTimeout(() => {
    handleCartLoaded();
    document.dispatchEvent(new CustomEvent("cartDrawerAddon:render"));
  }, 300);
  setCartDrawerLoadingState(false);
});

document.addEventListener("RenderCartSection", () => {
  setTimeout(() => {
    handleCartLoaded();
    document.dispatchEvent(new CustomEvent("cartDrawerAddon:render"));
  }, 300);
  setCartDrawerLoadingState(false);
});

document.addEventListener(ThemeEvents.cartError, () => {
  setCartDrawerLoadingState(false);
});

/**
 * @typedef {object} CartDrawerComponent
 * @property {Function} open - Method to open the cart drawer
 */

document.addEventListener(CartAddEvent.eventName, () => {
  setTimeout(() => {
    const cartItemsComponent = /**@type {HTMLElement} */ (
      document.querySelector("cart-items-component")
    );
    const { productsCount } = cartItemsComponent?.dataset || {};

    const cartDrawer = /**@type {CartDrawerComponent|null} */ (
      document.querySelector("cart-drawer-component")
    );

    if (cartDrawer) {
      if (
        discount_2NEW_collections.includes(Theme.collection?.id) ||
        discount_BARK_collections.includes(Theme.collection?.id) ||
        discount_MYSTRY_collections.includes(Theme.collection?.id) ||
        discount_NEWSHP_collections.includes(Theme.collection?.id) ||
        discount_FREEBUNDLE_collections.includes(Theme.collection?.id)
      ) {
        if (Number(productsCount) >= 2) {
          cartDrawer.open();
        }
        return;
      }

      cartDrawer.open();
    }
  }, 300);
});

/**
 * Observes cart line item errors showing
 */
function observeCartLineItemInlineErrors() {
  const cartDrawerComponent = document.querySelector("cart-drawer-component");

  if (cartDrawerComponent) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.target instanceof Element &&
          mutation.type === "attributes" &&
          mutation.target.classList.contains("cart-items__error") &&
          !mutation.target.classList.contains("hidden")
        ) {
          setCartDrawerLoadingState(false);
        }
      });
    });

    observer.observe(cartDrawerComponent, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
    });
  }
}

/**
 * Reads the combo config rendered by cart-gift-combo-config.liquid.
 * @returns {{
 *   boardVariantId: number,
 *   comboVariantIds: number[],
 *   swappableVariantIds: number[],
 *   comboMode: boolean,
 *   comboOptions: any[]
 * }}
 */
function getGiftComboConfig() {
  const comboElement = document.getElementById("cart_gift_combo_config");

  if (comboElement) {
    try {
      const config = JSON.parse(comboElement.textContent || "{}");
      const boardVariantId = Number(config.boardVariantId) || 0;
      const comboVariantIds = Array.isArray(config.comboVariantIds)
        ? config.comboVariantIds.map((/** @type {number|string} */ id) =>
            Number(id),
          )
        : [];
      const comboOptions = Array.isArray(config.comboOptions)
        ? config.comboOptions
        : [];

      return {
        boardVariantId,
        comboVariantIds,
        swappableVariantIds: [boardVariantId, ...comboVariantIds],
        comboMode: config.comboMode === true,
        comboOptions,
      };
    } catch {
      console.warn("Gift combo config parsing failed");
    }
  }

  return {
    boardVariantId: 0,
    comboVariantIds: [],
    swappableVariantIds: [],
    comboMode: false,
    comboOptions: [],
  };
}

/**
 * @returns {{ items: any[] } | null}
 */
function getCartJsonData() {
  const cartDataElement = document.getElementById("cart_json_data");

  if (!cartDataElement) return null;

  try {
    return JSON.parse(cartDataElement.textContent || "{}");
  } catch {
    console.warn("Cart json data parsing failed");
    return null;
  }
}

function clearComboButtonLoading() {
  document
    .querySelectorAll("[data-gift-combo-pick], [data-gift-combo-restore]")
    .forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      button.classList.remove("is-loading");
      button.removeAttribute("aria-busy");
      button.disabled = false;
    });
}

/**
 * @param {HTMLButtonElement} activeButton
 */
function setComboButtonLoading(activeButton) {
  document
    .querySelectorAll("[data-gift-combo-pick], [data-gift-combo-restore]")
    .forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      button.disabled = true;
      const isActive = button === activeButton;
      button.classList.toggle("is-loading", isActive);
      button.toggleAttribute("aria-busy", isActive);
    });
}

/**
 * Swaps a gift line item: board -> combo (pick) or combo -> board (restore).
 * @param {number | string} newVariantId
 * @param {string} [lineKey]
 * @param {HTMLButtonElement} [activeButton]
 */
function swapGiftLineItem(newVariantId, lineKey, activeButton) {
  const cartData = getCartJsonData();
  if (!cartData?.items?.length) return;

  const { boardVariantId, comboVariantIds, swappableVariantIds, comboOptions } =
    getGiftComboConfig();
  const variantId = Number(newVariantId);
  const isRestoringBoard = variantId === boardVariantId;
  const isComboTarget = comboVariantIds.includes(variantId);

  if (!isComboTarget && !isRestoringBoard) {
    return;
  }

  const lineItem = cartData.items.find((item) => item.key === lineKey);
  if (!lineItem) return;

  const currentVariantId = Number(lineItem.variant_id);
  if (!swappableVariantIds.includes(currentVariantId)) return;

  if (currentVariantId === variantId) return;

  /** @type {Record<string, string>} */
  let properties = {};

  if (isRestoringBoard) {
    // Restore the original target line from the JSON snapshot saved at pick time.
    try {
      const restoreProps = JSON.parse(
        lineItem.properties?._combo_restore_props || "{}",
      );
      Object.entries(restoreProps).forEach(([key, value]) => {
        if (value != null && value !== "") {
          properties[key] = String(value);
        }
      });
    } catch {
      console.warn("Combo restore properties parsing failed");
    }
    delete properties._gift_swapped_from_board;
    delete properties._combo_restore_props;
    delete properties._combo_target_title;
    delete properties._combo_description;
  } else {
    // Pick: snapshot the target line's properties + carry combo display info,
    // so the swapped line can show the note and be restored later.
    // Also keep gift eligibility props so cart-gifts.liquid can remove the
    // combo when the qualifying discount / min amount no longer applies.
    const option = comboOptions.find(
      (opt) => Number(opt.variantId) === variantId,
    );
    const originalProps = lineItem.properties || {};
    properties = {
      _gift_swapped_from_board: "true",
      _combo_restore_props: JSON.stringify(originalProps),
      _combo_target_title: lineItem.product_title || lineItem.title || "",
      _combo_description: (option && option.description) || "",
    };

    [
      "_gift_purchase",
      "_gift_discount",
      "_gift_min_amount",
      "_gift_progress_bar_id",
    ].forEach((key) => {
      const value = originalProps[key];
      if (value != null && value !== "") {
        properties[key] = String(value);
      }
    });
  }

  const cartActions = new CartActions();

  if (activeButton instanceof HTMLButtonElement) {
    setComboButtonLoading(activeButton);
  }

  giftComboInProgress = true;

  const finishGiftCombo = () => {
    giftComboInProgress = false;
    clearComboButtonLoading();
    document.removeEventListener(ThemeEvents.cartError, abortGiftCombo);
    updateGiftComboVisibility();
  };

  const abortGiftCombo = () => {
    giftComboInProgress = false;
    clearComboButtonLoading();
    document.removeEventListener(ThemeEvents.cartError, abortGiftCombo);
  };

  document.addEventListener(ThemeEvents.cartError, abortGiftCombo);

  cartActions.swapGiftLineItemInCart(
    lineItem.key,
    variantId,
    properties,
    finishGiftCombo,
  );
}

/**
 * @param {Event} event
 */
function handleGiftComboClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const comboPickButton = target.closest("[data-gift-combo-pick]");
  if (comboPickButton instanceof HTMLButtonElement) {
    event.preventDefault();
    if (giftComboInProgress || comboPickButton.disabled) return;
    swapGiftLineItem(
      comboPickButton.dataset.giftComboPick || "",
      comboPickButton.dataset.lineKey,
      comboPickButton,
    );
    return;
  }

  const comboRestoreButton = target.closest("[data-gift-combo-restore]");
  if (comboRestoreButton instanceof HTMLButtonElement) {
    event.preventDefault();
    if (giftComboInProgress || comboRestoreButton.disabled) return;
    const { boardVariantId } = getGiftComboConfig();
    swapGiftLineItem(
      boardVariantId,
      comboRestoreButton.dataset.lineKey,
      comboRestoreButton,
    );
    return;
  }
}

function updateGiftComboVisibility() {
  setTimeout(() => {
    // @ts-ignore
    if (window["igCartDrawer"] !== true) return;

    const cartData = getCartJsonData();
    const { boardVariantId, comboVariantIds } = getGiftComboConfig();
    let hasBoardGift = false;
    let hasSwappedCombo = false;

    if (cartData?.items?.length) {
      cartData.items.forEach((item) => {
        if (Number(item.variant_id) === boardVariantId) {
          hasBoardGift = true;
        }

        if (
          item.properties?._gift_swapped_from_board &&
          comboVariantIds.includes(Number(item.variant_id))
        ) {
          hasSwappedCombo = true;
        }
      });
    }

    document.querySelectorAll("[data-gift-combo-picker]").forEach((row) => {
      row.classList.toggle("hidden", !hasBoardGift);
    });

    document
      .querySelectorAll("[data-gift-combo-restore-row]")
      .forEach((row) => {
        row.classList.toggle("hidden", !hasSwappedCombo);
      });
  }, 500);
}

function initGiftComboHandlers() {
  document.removeEventListener("click", handleGiftComboClick);
  document.addEventListener("click", handleGiftComboClick);
}

document.addEventListener("cartDrawerAddon:render", updateGiftComboVisibility);
document.addEventListener("RenderCartSection", () => {
  requestAnimationFrame(() => {
    updateGiftComboVisibility();
  });
});

// @ts-ignore
window.CartActions = new CartActions();
initGiftComboHandlers();
